import React, { useState } from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react';
import { staticURL } from 'core/url';
import { Link } from 'core/routing';

import {
  IconButton,
  Paragraph,
  Heading,
  Tooltip,
  Button,
  Icon,
  Box,
} from 'components';
import { useMemoOne } from 'use-memo-one';

import RelativeTime from 'components/RelativeTime';
import { markdown } from 'libs/editor/serializers/markdown';
import { Editor } from 'libs/editor';
import { useViewerControl } from 'libs/rendering/gltf/hooks';
import { useContextManager } from 'apps/context/hooks';

import { EditorStore } from 'libs/editor/store';
import { Edit } from 'icons';

import { Seen } from './Seen';
import { SentBy } from './SentBy';
import { ButtonGroup } from './ButtonGroup';

import {
  ChatMessageIcon,
  ReplyMessageIcon,
  ChatMessageStyled,
  SenderName,
  AvatarStyled,
  ButtonCollapse,
  ButtonDeleteMessage,
  BlockWrapper,
} from './chat.styled';

const ChatMessage = observer(props => {
  const { message, createdBy, canDelete, onDelete, isLastRead } = props;

  if (message == null) return null;

  const [isExpanded, setExpanded] = useState(false);
  const [isMouseOver, setMouseOver] = useState(false);

  const { body, messageType, referenceId, timestamp } = message;
  const pinId = referenceId != null ? referenceId : message.id;

  const viewerControl = useViewerControl();
  const contextManager = useContextManager();

  const pin = pinId && viewerControl?.cadFile?.notePad.GetNote(pinId);
  const currentCamera = viewerControl?.viewer?.settings.CurrentCamera();
  const belongsToTab = viewerControl?.pinBelongsToCamera(pin, currentCamera);

  const isSelected =
    referenceId && referenceId === viewerControl?.expandedPinId;
  const isHovered = referenceId && referenceId === viewerControl?.hoveredPinId;

  const pending = !message.id;
  // const bodyCollapsible = body.length > 300;
  const bodyCollapsible = false;

  // This is only for testing, delete it later
  const typesList = ['regular', 'annotation', 'payment', 'revision'];
  const typeIndex = null; // null by default, 2 set all as payment type, 3 set all as revision type

  const isActivity = createdBy?.username && messageType === 'activity';
  const isRFQ = messageType === 'rfq';
  const annotationNumber = null;

  const account = createdBy || {};

  if (!account?.avatar) {
    account.avatar = {};
  }

  if (!account.avatar?.src) {
    account.avatar.src =
      account.avatar.completed || account.avatar.completed === undefined
        ? account.avatar.url
        : account.avatar.localFile
        ? URL.createObjectURL(account.avatar.localFile)
        : '';
  }

  // Check: is this outdated?
  /* const getTypeByReferenceId = id =>
    atob(id)
      ?.split(':')[0]
      ?.toLowerCase(); */

  let type = typeIndex
    ? typesList[typeIndex]
    : isActivity && referenceId
    ? message.messageType
    : // ? getTypeByReferenceId(referenceId)
      null;

  // Override always the type if it is an activity one
  if (type == null && isActivity) {
    type = 'activity';
  }

  const bodyText =
    isActivity && type === 'annotation' && !body ? 'Added an annotation' : body;

  const PayBlock = () =>
    type === 'payment' && (
      <BlockWrapper>
        <Heading level={5} margin='large 0 0 0'>
          <>Final Quote*</>
        </Heading>
        <Heading level={3} size='large' margin='default 0'>
          <>{'00.00 €'}</>
        </Heading>
        <Button text={<>Pay</>} />
        <Button
          removeBold
          size='xsmall'
          appearance='plain'
          variant='minimal'
          margin='small 0 default 0'
          text={<>Cancel or Refund</>}
        />
      </BlockWrapper>
    );

  const RevisonBlock = () =>
    type === 'revision' && (
      <BlockWrapper>
        <Heading
          level={4}
          size='xsmall'
          weight='regular'
          margin='large 0 default 0'
        >
          <>Your design needs some changes. Please upload an updated version.</>
        </Heading>
        <Button
          outline
          variant='tertiary'
          size='small'
          margin='small 0 default 0'
          text={<>Upload Revision</>}
        />
      </BlockWrapper>
    );

  const pinTargetType =
    referenceId != null && viewerControl?.pinTargetType(referenceId);

  const editorStore = useMemoOne(() => {
    return new EditorStore({
      formatting: 'markdown',
      initialValue: markdown.deserialize(bodyText),
      readOnly: true,
    });
  }, []);

  const handleSaveBtnClick = () => {
    editorStore.setMarkdownValue();
    if (editorStore.markdownValue !== '') {
      contextManager.UpdateMessage(message.id, editorStore.markdownValue);
      editorStore.SetEditable(false);
    }
  };

  const handleCancelBtnClick = () => {
    editorStore.onChange({ value: markdown.deserialize(bodyText) });
    editorStore.SetEditable(false);
  };

  const handleEditBtnClick = () => {
    editorStore.SetEditable(true);
  };

  const handleMouseOver = () => setMouseOver(true);
  const handleMouseLeave = () => setMouseOver(false);

  let avatarSrc = account.avatar?.url;
  let avatarName = account.fullName;
  if (isRFQ) {
    avatarSrc = staticURL('assets/logos/wikifactory-black.png');
    avatarName = 'Wikifactory';
  }

  let senderNameElement = (
    <SenderName level={4} size='xsmall'>
      {isRFQ === true ? (
        <span>Wikifactory&nbsp;</span>
      ) : (
        <Link name='profile' params={{ profile: createdBy?.username }}>
          {createdBy?.username}{' '}
        </Link>
      )}
      <RelativeTime date={timestamp} />
    </SenderName>
  );
  let sentByElement = (
    <>
      {message.sentAs ? (
        <SentBy
          createdBy={createdBy.username}
          sentAsName={message?.sentAs.username}
        />
      ) : null}
    </>
  );
  let seenElement = <>{message.readByCustomer ? <Seen /> : null}</>;
  let buttonsGroupElement = null;
  let editorStyle = {};
  let editorWrapperStyle = {
    display: 'flex',
    alignItems: 'baseline',
  };

  const isEditable = !editorStore.readOnly;

  if (isEditable) {
    senderNameElement = null;
    sentByElement = null;
    seenElement = null;
    editorStyle = { background: 'white' };
    editorWrapperStyle = {
      display: 'block',
    };
  }

  if (isMouseOver) {
    buttonsGroupElement = (
      <ButtonGroup>
        <IconButton
          onClick={handleEditBtnClick}
          size='small'
          icon={<Edit />}
          variant='outlined'
        />
      </ButtonGroup>
    );
  }

  return (
    <>
      <ChatMessageStyled
        editable={isEditable}
        pending={pending}
        index={props.index}
        active={belongsToTab}
        expanded={isSelected}
        onMouseLeave={handleMouseLeave}
        onMouseEnter={handleMouseOver}
        type={type}
      >
        {!isEditable ? (
          buttonsGroupElement
        ) : (
          <Box
            sx={{
              position: 'absolute',
              display: 'flex',
              right: '1rem',
              bottom: '0.25rem',
            }}
          >
            <Button
              onClick={handleCancelBtnClick}
              text='Cancel'
              size='xsmall'
              variant='tertiary'
              outline
              style={{
                minWidth: 'auto',
                marginRight: '0.5rem',
                boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.1)',
              }}
            />
            <Button
              onClick={handleSaveBtnClick}
              text='Save'
              size='xsmall'
              outline
              style={{
                minWidth: 'auto',
                boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.1)',
              }}
            />
          </Box>
        )}
        <Tooltip delayShow={500} place='left' />
        {type === 'annotation' || isActivity ? (
          <ChatMessageIcon
            type={type}
            active={belongsToTab}
            targetType={pinTargetType}
            annotationNumber={annotationNumber}
            highlighted={isHovered}
            expanded={isSelected}
          >
            <Icon
              name={
                pinTargetType === 'part'
                  ? 'annotation_part'
                  : pinTargetType === 'feature'
                  ? 'annotation_feature'
                  : 'annotation'
              }
              size={type === 'annotation' || isActivity ? 'large' : 'default'}
              viewBox='0 0 40 40'
              onClick={
                belongsToTab
                  ? () => viewerControl?.TogglePinExpand(referenceId)
                  : null
              }
              onMouseEnter={
                belongsToTab
                  ? () => viewerControl?.HighlightPinTarget(referenceId)
                  : null
              }
              onMouseLeave={
                belongsToTab ? viewerControl?.UnHighlightPinTarget : null
              }
            />
          </ChatMessageIcon>
        ) : type === 'automated' ? (
          <ChatMessageIcon
            type={type}
            active={belongsToTab}
            targetType={pinTargetType}
            annotationNumber={annotationNumber}
            highlighted={isHovered}
            expanded={isSelected}
          >
            <img
              src={staticURL('assets/chat/botler.svg')}
              alt='Botler'
              style={{ borderRadius: '50%' }}
            />
          </ChatMessageIcon>
        ) : referenceId ? (
          <ReplyMessageIcon
            onClick={() => viewerControl?.TogglePinExpand(referenceId)}
            onMouseEnter={() => viewerControl?.HighlightPinTarget(referenceId)}
            onMouseLeave={viewerControl?.UnHighlightPinTarget}
          >
            <AvatarStyled src={avatarSrc} name={avatarName} />
          </ReplyMessageIcon>
        ) : (
          <Link name='profile' params={{ profile: createdBy?.username }}>
            <AvatarStyled src={avatarSrc} name={avatarName} />
          </Link>
        )}
        <div>
          <Box
            sx={{
              justifyContent: 'space-between',
              alignItems: 'center',
              display: 'flex',
            }}
          >
            {senderNameElement}
            {seenElement}
          </Box>
          {bodyText && type !== 'payment' && type !== 'revision' && (
            <Box sx={editorWrapperStyle}>
              <Editor
                store={editorStore}
                placeholder='Your message here'
                previewMode='chat'
                style={editorStyle}
              />
              {!isEditable && message.updatedAt ? (
                <Paragraph size='xsmall' variant='light' ml={2}>
                  (edited)
                </Paragraph>
              ) : null}
            </Box>
          )}
          {sentByElement}
          <PayBlock />
          <RevisonBlock />
        </div>
        {bodyCollapsible && (
          <ButtonCollapse
            size='xsmall'
            icon={isExpanded ? 'caret_up' : 'caret_down'}
            data-tip='Expand'
            onClick={() => setExpanded(!isExpanded)}
          />
        )}
        {canDelete && messageType === 'regular' && !pending && (
          <ButtonDeleteMessage
            icon='delete'
            size='xsmall'
            data-tip='Delete'
            ariaLabel='Delete Messages'
            appearance='plain'
            variant='destructive'
            onClick={() => onDelete()}
          />
        )}
      </ChatMessageStyled>
      {isLastRead && (
        <>
          <NewMessageGroup className='test'>
            <FillerLine />
            <ScrollDownWrapper>
              <StyledP>New</StyledP>
              <Button
                outline
                icon='arrow_down'
                size='xsmall'
                variant='minimal'
                onClick={() => {
                  props.onScrollToBottom({ setRead: true });
                }}
              />
            </ScrollDownWrapper>
          </NewMessageGroup>
        </>
      )}
    </>
  );
});

const NewMessageGroup = styled.div`
  display: flex;
  align-items: center;
  position: relative;
`;

const ScrollDownWrapper = styled.div`
  right: 24px;
  position: absolute;
  background: #fff;
  align-items: center;
  padding: 0 8px;
  display: flex;
  z-index: 200;
`;
const FillerLine = styled.div`
  height: 1px;
  position: relative;
  background-color: #c5c5c5;
  width: calc(100% - 16px);
  transform: translateX(-50%);
  left: 50%;
`;

const StyledP = styled.p`
  font-size: 14px;
  margin-right: 8px;
  color: #8b8b8b;
`;

export default ChatMessage;
